import React, { useMemo } from "react";
import ReactSelect from "react-select";
import { TEMPLATE_LANGUAGES } from "../constants/templateLanguages";

const LANGUAGE_OPTIONS = TEMPLATE_LANGUAGES.map(({ code, label }) => ({
  value: code,
  label,
  searchText: `${label} ${code}`.toLowerCase(),
}));

const filterOption = (option, inputValue) => {
  if (!inputValue) return true;
  const q = inputValue.toLowerCase().trim();
  return (
    option.data.searchText.includes(q) ||
    option.label.toLowerCase().includes(q) ||
    option.value.toLowerCase().includes(q)
  );
};

const baseStyles = {
  indicatorSeparator: () => ({ display: "none" }),
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
  menuList: (base) => ({
    ...base,
    maxHeight: 240,
    paddingTop: 4,
    paddingBottom: 4,
  }),
};

const shortLanguageLabel = (label) => label.split(" (")[0];

const sizeStyles = {
  sm: {
    control: (base, state) => ({
      ...base,
      minHeight: 38,
      fontSize: 14,
      borderColor: state.isFocused ? "#1877f2" : "#ccd0d5",
      borderRadius: 3,
      boxShadow: state.isFocused ? "0 0 0 1px #1877f2" : "none",
      "&:hover": { borderColor: state.isFocused ? "#1877f2" : "#bcc0c4" },
    }),
    valueContainer: (base) => ({ ...base, padding: "0 10px" }),
    input: (base) => ({ ...base, margin: 0, padding: 0 }),
    dropdownIndicator: (base) => ({ ...base, padding: "0 8px", color: "#65676b" }),
    option: (base, state) => ({
      ...base,
      fontSize: 13,
      padding: "8px 12px",
      backgroundColor: state.isSelected ? "#e7f3ff" : state.isFocused ? "#f0f2f5" : "white",
      color: state.isSelected ? "#1877f2" : "#050505",
    }),
    singleValue: (base) => ({ ...base, color: "#050505" }),
    placeholder: (base) => ({ ...base, color: "#65676b", fontSize: 13 }),
  },
  meta: {
    control: (base, state) => ({
      ...base,
      minHeight: 38,
      fontSize: 15,
      borderColor: state.isFocused ? "#1877f2" : "#ccd0d5",
      borderRadius: 3,
      boxShadow: state.isFocused ? "0 0 0 1px #1877f2" : "none",
      backgroundColor: "white",
      "&:hover": { borderColor: state.isFocused ? "#1877f2" : "#bcc0c4" },
    }),
    valueContainer: (base) => ({ ...base, padding: "0 8px" }),
    input: (base) => ({ ...base, margin: 0, padding: 0 }),
    dropdownIndicator: (base) => ({ ...base, padding: "0 6px", color: "#65676b" }),
    option: (base, state) => ({
      ...base,
      fontSize: 14,
      padding: "8px 12px",
      backgroundColor: state.isSelected ? "#e7f3ff" : state.isFocused ? "#f0f2f5" : "white",
      color: state.isSelected ? "#1877f2" : "#050505",
    }),
    singleValue: (base) => ({ ...base, color: "#050505" }),
    placeholder: (base) => ({ ...base, color: "#65676b" }),
  },
  md: {
    control: (base, state) => ({
      ...base,
      minHeight: 42,
      fontSize: 14,
      fontWeight: 500,
      borderColor: state.isFocused ? "#9ca3af" : "#d1d5db",
      borderRadius: 12,
      boxShadow: state.isFocused ? "0 0 0 2px rgba(156, 163, 175, 0.4)" : "none",
      "&:hover": { borderColor: "#9ca3af" },
    }),
    valueContainer: (base) => ({ ...base, padding: "0 14px" }),
    input: (base) => ({ ...base, margin: 0, padding: 0 }),
    dropdownIndicator: (base) => ({ ...base, padding: "0 10px" }),
    option: (base, state) => ({
      ...base,
      fontSize: 14,
      padding: "10px 14px",
      backgroundColor: state.isSelected ? "#E7FCE8" : state.isFocused ? "#f3f4f6" : "white",
      color: state.isSelected ? "#006e5a" : "#111827",
    }),
    singleValue: (base) => ({ ...base, color: "#111827" }),
    placeholder: (base) => ({ ...base, color: "#9ca3af" }),
  },
};

/**
 * Searchable language picker for WhatsApp template creation.
 * Menu is capped at 240px height (scrollable) and rendered in a portal.
 */
export default function TemplateLanguageSelect({
  value,
  onChange,
  size = "sm",
  placeholder = "Search language…",
  isDisabled = false,
}) {
  const selected = useMemo(
    () => LANGUAGE_OPTIONS.find((o) => o.value === value) || null,
    [value]
  );

  const styles = useMemo(
    () => ({
      ...baseStyles,
      ...sizeStyles[size],
    }),
    [size]
  );

  return (
    <ReactSelect
      options={LANGUAGE_OPTIONS}
      value={selected}
      onChange={(opt) => onChange(opt?.value || "")}
      isSearchable
      isClearable={false}
      isDisabled={isDisabled}
      placeholder={placeholder}
      filterOption={filterOption}
      maxMenuHeight={240}
      menuPortalTarget={typeof document !== "undefined" ? document.body : null}
      menuPlacement="auto"
      menuPosition="fixed"
      styles={styles}
      formatOptionLabel={({ label, value: code }, { context }) =>
        context === "value" ? (
          shortLanguageLabel(label)
        ) : (
          <span>
            {label}
            <span className="text-[#65676b] ml-1.5 text-xs font-normal">({code})</span>
          </span>
        )
      }
    />
  );
}
